import * as React from "react";
import { useAction } from "convex/react";
import {
  Download,
  FileText,
  Image as ImageIcon,
  Loader2,
  Split,
  Upload,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/convexApi";
import {
  BILL_DOCUMENT_HELP_TEXT,
  BILL_DOCUMENT_INPUT_ACCEPT,
  uploadBillDocumentDirectly,
} from "@/lib/bill-document-upload";
import { cn } from "@/lib/utils";
import { SplitPdfDialog, type PdfPageRange } from "./split-pdf-dialog";

const PdfDocumentViewer = React.lazy(async () => {
  const module = await import("./pdf-document-viewer");
  return { default: module.PdfDocumentViewer };
});

export interface BillDocumentSummary {
  _id: string;
  filename: string;
  originalFilename?: string;
  contentType: string;
  fileSize: number;
  sha256: string;
  createdAt: number;
}

interface SignedDocumentUrl {
  url: string;
  expiresAt: number;
  contentType: string;
  filename: string;
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

export function DocumentPanel({
  billId,
  documents,
  canEdit,
  beforeSplit,
  onSplitComplete,
}: {
  billId: string;
  documents: BillDocumentSummary[];
  canEdit: boolean;
  beforeSplit?: () => boolean;
  onSplitComplete?: (firstBillId: string) => void;
}) {
  const createBillDocumentUploadTarget = useAction(
    (api.accountingBillDocuments as any).createBillDocumentR2UploadUrl,
  );
  const finalizeBillDocumentUpload = useAction(
    (api.accountingBillDocuments as any).finalizeBillDocumentR2Upload,
  );
  const getBillDocumentUrl = useAction(
    api.accountingBillDocuments.getBillDocumentUrl,
  );
  const splitBillPdf = useAction(api.accountingBillDocuments.splitBillPdf);

  const [selectedId, setSelectedId] = React.useState<string | null>(
    documents[0]?._id ?? null,
  );
  const [signedUrl, setSignedUrl] = React.useState<SignedDocumentUrl | null>(
    null,
  );
  const [isLoadingUrl, setIsLoadingUrl] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const [pdfPageCount, setPdfPageCount] = React.useState(0);
  const [splitOpen, setSplitOpen] = React.useState(false);
  const [isSplitting, setIsSplitting] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const urlRequestRef = React.useRef(0);

  // Keep the selection valid as documents change.
  React.useEffect(() => {
    if (documents.length === 0) {
      urlRequestRef.current += 1;
      setSelectedId(null);
      setSignedUrl(null);
      return;
    }
    if (!selectedId || !documents.some((doc) => doc._id === selectedId)) {
      setSelectedId(documents[0]!._id);
    }
  }, [documents, selectedId]);

  const fetchUrl = React.useCallback(
    async (documentId: string) => {
      const request = urlRequestRef.current + 1;
      urlRequestRef.current = request;
      setIsLoadingUrl(true);
      setSignedUrl(null);
      setPdfPageCount(0);
      try {
        const result = (await getBillDocumentUrl({
          documentId,
        })) as SignedDocumentUrl;
        if (urlRequestRef.current === request) setSignedUrl(result);
      } catch (err) {
        if (urlRequestRef.current === request) {
          setSignedUrl(null);
          toast.error(
            err instanceof Error ? err.message : "Could not open the document",
          );
        }
      } finally {
        if (urlRequestRef.current === request) setIsLoadingUrl(false);
      }
    },
    [getBillDocumentUrl],
  );

  // Signed URLs are short-lived: fetch on selection, never persist them.
  React.useEffect(() => {
    if (!selectedId) return;
    void fetchUrl(selectedId);
  }, [selectedId, fetchUrl]);

  // Force a re-render right as the signed URL goes stale so the "expired"
  // fallback surfaces on a long-open preview instead of only on state changes.
  const [, forceExpiryCheck] = React.useReducer((tick: number) => tick + 1, 0);
  React.useEffect(() => {
    if (!signedUrl) return;
    const msUntilStale = signedUrl.expiresAt - 5_000 - Date.now();
    if (msUntilStale <= 0) return;
    const timer = window.setTimeout(forceExpiryCheck, msUntilStale);
    return () => window.clearTimeout(timer);
  }, [signedUrl]);

  const uploadFiles = async (files: FileList | File[]) => {
    if (!canEdit) return;
    setIsUploading(true);
    try {
      for (const file of Array.from(files)) {
        await uploadBillDocumentDirectly({
          billId,
          file,
          createUploadTarget: createBillDocumentUploadTarget,
          finalizeUpload: finalizeBillDocumentUpload,
        });
      }
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSplit = async (ranges: PdfPageRange[]) => {
    if (!selectedDoc) return;
    setIsSplitting(true);
    try {
      const result = (await splitBillPdf({
        billId,
        documentId: selectedDoc._id,
        ranges,
      })) as { billIds: string[] };
      const firstBillId = result.billIds[0];
      if (!firstBillId) throw new Error("The split did not create any bills");
      toast.success(
        `PDF split into ${result.billIds.length} bill${result.billIds.length === 1 ? "" : "s"}`,
      );
      setSplitOpen(false);
      onSplitComplete?.(firstBillId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not split the PDF");
    } finally {
      setIsSplitting(false);
    }
  };

  const selectedDoc = documents.find((doc) => doc._id === selectedId) ?? null;
  const urlIsFresh =
    signedUrl !== null && signedUrl.expiresAt > Date.now() + 5_000;

  return (
    <Card
      className={cn(
        // overflow-hidden: inside the detail page's fixed-height sticky
        // wrapper, the min-h preview must clip at the card edge on short
        // viewports instead of spilling past it.
        "flex h-full flex-col gap-3 overflow-hidden rounded-xl border p-3",
        isDragging && "border-primary bg-primary/5",
      )}
      onDragOver={(e) => {
        if (!canEdit) return;
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={(e) => {
        if (!canEdit) return;
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files.length > 0) {
          void uploadFiles(e.dataTransfer.files);
        }
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Documents</h2>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {selectedDoc && signedUrl && urlIsFresh && (
            <Button variant="outline" size="sm" asChild>
              <a
                href={signedUrl.url}
                download={signedUrl.filename}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Download ${selectedDoc.originalFilename ?? selectedDoc.filename}`}
              >
                <Download className="mr-2 h-3.5 w-3.5" />
                Download
              </a>
            </Button>
          )}
          {canEdit && (
            <>
              <Button
                variant="outline"
                size="sm"
                disabled={isUploading}
                onClick={() => fileInputRef.current?.click()}
              >
                {isUploading ? (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Upload className="mr-2 h-3.5 w-3.5" />
                )}
                Upload
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept={BILL_DOCUMENT_INPUT_ACCEPT}
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    void uploadFiles(e.target.files);
                  }
                  e.target.value = "";
                }}
              />
              {selectedDoc?.contentType === "application/pdf" && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={pdfPageCount < 2}
                  title={
                    pdfPageCount < 2
                      ? "A PDF needs at least two pages to split"
                      : documents.length > 1
                        ? "Split this PDF; other attachments stay on the original bill"
                        : "Split this PDF into individual bills"
                  }
                  onClick={() => {
                    if (beforeSplit && !beforeSplit()) return;
                    setSplitOpen(true);
                  }}
                >
                  <Split className="mr-2 h-3.5 w-3.5" />
                  Split PDF
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {documents.length === 0 ? (
        <div className="text-muted-foreground flex flex-1 flex-col items-center justify-center gap-2 rounded-md border border-dashed p-8 text-center text-sm">
          <FileText className="h-8 w-8" />
          <p>No documents attached.</p>
          {canEdit && <p className="text-xs">{BILL_DOCUMENT_HELP_TEXT}</p>}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5">
            {documents.map((doc) => {
              const isImage = doc.contentType.startsWith("image/");
              const label = doc.originalFilename ?? doc.filename;
              return (
                <div
                  key={doc._id}
                  className={cn(
                    "flex items-center gap-1 rounded-md border px-2 py-1 text-xs",
                    doc._id === selectedId && "border-primary bg-primary/5",
                  )}
                >
                  <button
                    type="button"
                    className="flex items-center gap-1.5"
                    onClick={() => setSelectedId(doc._id)}
                    title={`${label} (${formatFileSize(doc.fileSize)})`}
                  >
                    {isImage ? (
                      <ImageIcon className="h-3.5 w-3.5" />
                    ) : (
                      <FileText className="h-3.5 w-3.5" />
                    )}
                    <span className="max-w-40 truncate">{label}</span>
                  </button>
                </div>
              );
            })}
          </div>

          <div className="min-h-96 flex-1 overflow-hidden rounded-md border bg-muted/30">
            {isLoadingUrl ? (
              <div className="flex h-full items-center justify-center">
                <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
              </div>
            ) : !selectedDoc || !signedUrl ? (
              <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
                Select a document to preview it.
              </div>
            ) : !urlIsFresh ? (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-sm">
                <p className="text-muted-foreground">
                  The preview link expired.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void fetchUrl(selectedDoc._id)}
                >
                  Reload preview
                </Button>
              </div>
            ) : signedUrl.contentType.startsWith("image/") ? (
              <div className="h-full overflow-auto p-2">
                <img
                  src={signedUrl.url}
                  alt={signedUrl.filename}
                  className="mx-auto max-w-full"
                />
              </div>
            ) : (
              <React.Suspense
                fallback={
                  <div className="flex h-full items-center justify-center" role="status">
                    <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
                    <span className="sr-only">Loading PDF viewer</span>
                  </div>
                }
              >
                <PdfDocumentViewer
                  key={signedUrl.url}
                  sourceUrl={signedUrl.url}
                  filename={signedUrl.filename}
                  onPageCountChange={setPdfPageCount}
                />
              </React.Suspense>
            )}
          </div>
        </>
      )}

      {selectedDoc?.contentType === "application/pdf" && (
        <SplitPdfDialog
          open={splitOpen}
          onOpenChange={setSplitOpen}
          filename={selectedDoc.originalFilename ?? selectedDoc.filename}
          totalPages={pdfPageCount}
          isSubmitting={isSplitting}
          onConfirm={handleSplit}
        />
      )}
    </Card>
  );
}
